import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Upload, Save, ArrowLeft, Image as ImageIcon, FileText,
  CheckCircle, AlertCircle, Plus, Trash2, Radio, Monitor, X, User, Video,
  BookOpen, Link as LinkIcon, ClipboardList, ChevronDown, ChevronUp, Download,
  Sparkles, Archive, Package, Megaphone, FileImage, Users2, MapPin, CalendarDays, Mic, GripVertical, QrCode,
  Smartphone, ExternalLink,
} from 'lucide-react';
import { api, apiUpload } from '../../lib/api';
import { formatAgendaTime, LIVE_ONLY_META } from '../../lib/agenda-pdf';
import DateTimePicker from '../../components/admin/DateTimePicker';
import EngineContentCard from '../../components/admin/EngineContentCard';
import { utcToEastern, easternToUtc } from '../../lib/eastern-time';

const EMPTY = {
  course_type: 'async',
  content_format: 'scorm',
  slug: '',
  title: '',
  short_description: '',
  long_description_md: '',
  price_dollars: '',
  currency: 'USD',
  duration_minutes: '',
  status: 'draft',
  is_featured: false,
  display_order: 0,
  live_date: '',
  hosted_by: '',
  hosted_by_title: '',
  hosted_by_bio: '',
  what_youll_learn: [],
  live_roadmap_md: '',
  live_roadmap_description: '',
  live_roadmap_objectives: '',
  live_roadmap_flow: '',
  zoom_meeting_id: '',
  zoom_join_url: '',
  min_attendance_minutes: 30,
  evaluation_questions: [],
  venue_name: '',
  venue_address: '',
  in_person_capacity: '',
  lunch_included: false,
  conference_checkin: false,
  brand_name: '',
  brand_color: '',
  brand_accent_color: '',
  live_date_utc: '',
  ceu_hours: '',
  lpca_approval_number: '',
  gaca_approval_number: '',
  learning_objectives: [],
  use_default_cert_template: true,
};

export default function CourseEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [form, setForm] = useState(EMPTY);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  // Result of moving the linked Zoom meeting after a date/length change.
  const [zoomNotice, setZoomNotice] = useState(null);
  const [error, setError] = useState(null);

  // Pending file picks — held in state until save
  const [pendingHero, setPendingHero] = useState(null);
  const [pendingScorm, setPendingScorm] = useState(null);
  const [pendingCert, setPendingCert] = useState(null);
  const [pendingPresenterImg, setPendingPresenterImg] = useState(null);
  const [pendingVideo, setPendingVideo] = useState(null);
  const [pendingResource, setPendingResource] = useState(null);
  const [pendingFeaturedImg, setPendingFeaturedImg] = useState(null);
  const [pendingSocialImg, setPendingSocialImg] = useState(null);
  const [pendingDripFiles, setPendingDripFiles] = useState([]);
  const [pendingPromoFiles, setPendingPromoFiles] = useState([]);
  const [pendingAgenda, setPendingAgenda] = useState([]);
  const [creatingZoom, setCreatingZoom] = useState(false);
  const [zoomError, setZoomError] = useState(null);
  const [deletingZoom, setDeletingZoom] = useState(false);
  const heroRef = useRef(null);
  const scormRef = useRef(null);
  const certRef = useRef(null);
  const presenterImgRef = useRef(null);
  const videoRef = useRef(null);
  const resourceRef = useRef(null);
  const featuredImgRef = useRef(null);
  const socialImgRef = useRef(null);
  // Minimum attendance mirrors the live-session runtime until an admin gives it
  // a distinct value; once it does, we stop auto-filling so their override sticks.
  const minAttendanceTouched = useRef(false);

  useEffect(() => {
    if (isNew) return;
    api('/api/admin/courses')
      .then(({ courses }) => {
        const c = courses.find((x) => x.id === id);
        if (!c) {
          setError('Course not found');
          return;
        }
        setCourse(c);
        setForm({
          course_type: c.course_type || 'async',
          content_format: c.content_format || 'scorm',
          venue_name: c.venue_name || '',
          venue_address: c.venue_address || '',
          in_person_capacity: c.in_person_capacity ?? '',
          lunch_included: !!c.lunch_included,
          conference_checkin: !!c.conference_checkin,
          brand_name: c.brand_name || '',
          brand_color: c.brand_color || '',
          brand_accent_color: c.brand_accent_color || '',
          slug: c.slug,
          title: c.title,
          short_description: c.short_description || '',
          long_description_md: c.long_description_md || '',
          price_dollars: c.price_cents ? (c.price_cents / 100).toFixed(2) : '',
          currency: c.currency,
          duration_minutes: c.duration_minutes ?? '',
          status: c.status,
          is_featured: c.is_featured,
          display_order: c.display_order,
          live_date: c.live_date || '',
          hosted_by: c.hosted_by || '',
          hosted_by_title: c.hosted_by_title || '',
          hosted_by_bio: c.hosted_by_bio || '',
          what_youll_learn: c.what_youll_learn || [],
          live_roadmap_md: c.live_roadmap_md || '',
          live_roadmap_description: c.live_roadmap_description || '',
          live_roadmap_objectives: c.live_roadmap_objectives || '',
          live_roadmap_flow: c.live_roadmap_flow || '',
          zoom_meeting_id: c.zoom_meeting_id || '',
          zoom_join_url: c.zoom_join_url || '',
          min_attendance_minutes: c.min_attendance_minutes ?? 30,
          evaluation_questions: c.evaluation_questions || [],
          live_date_utc: c.live_date_utc ? utcToEastern(c.live_date_utc) : '',
          ceu_hours: c.ceu_hours ?? '',
          lpca_approval_number: c.lpca_approval_number || '',
          gaca_approval_number: c.gaca_approval_number || '',
          learning_objectives: c.learning_objectives || [],
          use_default_cert_template: c.use_default_cert_template ?? true,
        });
        // If the saved minimum already differs from the runtime, treat it as an
        // intentional override and don't auto-mirror; otherwise keep them linked.
        minAttendanceTouched.current = (c.min_attendance_minutes ?? 30) != (c.duration_minutes ?? '');
      })
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    setZoomNotice(null);
    setSaveStatus('Saving course…');
    try {
      const payload = {
        ...form,
        price_cents: Math.round(parseFloat(form.price_dollars || '0') * 100) || 0,
        display_order: parseInt(form.display_order, 10) || 0,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes, 10) : null,
        what_youll_learn: form.what_youll_learn.filter((b) => b.trim()),
        learning_objectives: form.learning_objectives.filter((b) => b.trim()),
        ceu_hours: form.ceu_hours ? parseFloat(form.ceu_hours) : null,
        in_person_capacity: form.in_person_capacity === '' ? null : parseInt(form.in_person_capacity, 10),
        min_attendance_minutes: parseInt(form.min_attendance_minutes, 10) || 30,
        evaluation_questions: form.evaluation_questions,
        live_date_utc: form.live_date_utc ? easternToUtc(form.live_date_utc) : null,
      };
      delete payload.zoom_session_title;

      let courseId = id;
      if (isNew) {
        const { course: created } = await api('/api/admin/courses', { method: 'POST', body: payload });
        courseId = created.id;
        setCourse(created);
      } else {
        const { course: updated, zoom_sync: zoomSync } = await api(`/api/admin/courses/${id}`, { method: 'PATCH', body: payload });
        setCourse(updated);
        setZoomNotice(zoomSync ? zoomSyncNotice(zoomSync) : null);
      }

      // Upload any pending files in parallel
      const uploads = [];
      if (pendingHero) {
        uploads.push(
          uploadFile(`/api/admin/courses/${courseId}/hero-image-upload`, 'image', pendingHero, 'Uploading image…')
        );
      }
      if (pendingScorm) {
        uploads.push(
          uploadFile(`/api/admin/courses/${courseId}/scorm-upload`, 'scorm', pendingScorm, 'Uploading SCORM…')
        );
      }
      if (pendingCert) {
        uploads.push(
          uploadFile(`/api/admin/courses/${courseId}/cert-template-upload`, 'template', pendingCert)
        );
      }
      if (pendingPresenterImg) {
        uploads.push(
          uploadFile(`/api/admin/courses/${courseId}/presenter-image-upload`, 'image', pendingPresenterImg)
        );
      }
      if (pendingVideo) {
        uploads.push(
          uploadFile(`/api/admin/courses/${courseId}/video-upload`, 'video', pendingVideo, 'Uploading video…')
        );
      }
      if (pendingResource) {
        uploads.push(
          uploadFile(`/api/admin/courses/${courseId}/resource-upload`, 'resource', pendingResource, 'Uploading resource…')
        );
      }
      if (pendingFeaturedImg) {
        uploads.push(
          uploadFile(`/api/admin/courses/${courseId}/featured-image-upload`, 'image', pendingFeaturedImg, 'Uploading featured image…')
        );
      }
      if (pendingSocialImg) {
        uploads.push(
          uploadFile(`/api/admin/courses/${courseId}/social-image-upload`, 'image', pendingSocialImg, 'Uploading social share image…')
        );
      }

      if (uploads.length) {
        setSaveStatus(`Uploading ${uploads.length} file${uploads.length > 1 ? 's' : ''}…`);
        const results = await Promise.all(uploads);
        const merged = Object.assign({}, ...results);
        setCourse((c) => ({ ...c, ...merged }));
      }

      // Upload pending drip content
      if (pendingDripFiles.length) {
        setSaveStatus(`Uploading ${pendingDripFiles.length} drip file${pendingDripFiles.length > 1 ? 's' : ''}…`);
        for (const entry of pendingDripFiles) {
          const { drip_item } = await api(`/api/admin/courses/${courseId}/drip-content`, {
            method: 'POST',
            body: { title: entry.title, content_type: entry.content_type, delay_days: entry.delay_days, display_order: 0 },
          });
          const fd = new FormData();
          const fieldName = entry.content_type === 'scorm' ? 'scorm' : entry.content_type;
          fd.append(fieldName, entry.file);
          await apiUpload(`/api/admin/drip-content/${drip_item.id}/${entry.content_type}-upload`, fd);
        }
      }

      // Create agenda rows staged before the course existed
      if (pendingAgenda.length) {
        setSaveStatus(`Saving ${pendingAgenda.length} agenda item${pendingAgenda.length > 1 ? 's' : ''}…`);
        for (const { speaker_photo_files: photoFiles = [], ...item } of pendingAgenda) {
          const { agenda_item } = await api(`/api/admin/courses/${courseId}/agenda`, { method: 'POST', body: item });
          await uploadSpeakerPhotos(agenda_item.speakers, photoFiles);
        }
        setPendingAgenda([]);
      }

      // Upload promotional material staged before the course existed
      if (pendingPromoFiles.length) {
        setSaveStatus(`Uploading ${pendingPromoFiles.length} promo file${pendingPromoFiles.length > 1 ? 's' : ''}…`);
        for (const entry of pendingPromoFiles) {
          const fd = new FormData();
          fd.append('file', entry.file);
          fd.append('title', entry.title);
          await apiUpload(`/api/admin/courses/${courseId}/promo-materials`, fd);
        }
        setPendingPromoFiles([]);
      }

      // Clear pending files
      setPendingHero(null);
      setPendingScorm(null);
      setPendingCert(null);
      setPendingPresenterImg(null);
      setPendingVideo(null);
      setPendingResource(null);
      setPendingFeaturedImg(null);
      setPendingSocialImg(null);
      setPendingDripFiles([]);
      if (heroRef.current) heroRef.current.value = '';
      if (scormRef.current) scormRef.current.value = '';
      if (certRef.current) certRef.current.value = '';
      if (presenterImgRef.current) presenterImgRef.current.value = '';
      if (videoRef.current) videoRef.current.value = '';
      if (featuredImgRef.current) featuredImgRef.current.value = '';
      if (socialImgRef.current) socialImgRef.current.value = '';
      if (resourceRef.current) resourceRef.current.value = '';

      if (isNew) {
        navigate(`/admin/courses/${courseId}/edit`, { replace: true });
      }

      setSaveStatus(null);
    } catch (err) {
      setError(err.body?.detail || err.body?.error || err.message);
      setSaveStatus(null);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (error && !course && !isNew) return <div className="p-8 text-rose-600">{error}</div>;

  const isHybrid = form.course_type === 'hybrid';
  // A hybrid event runs the entire live pipeline (Zoom, attendance, evaluation,
  // certificate) and simply adds a room. Everything gated on isLive must stay
  // visible for it — hence "live-like" rather than an equality check.
  const isLive = form.course_type === 'live' || isHybrid;
  const isTutorial = form.course_type === 'tutorial';

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <Link to="/admin/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-2">
          <ArrowLeft size={14} /> Back to courses
        </Link>
        <h1 className="text-2xl font-bold">{isNew ? 'New course' : course?.title || 'Edit course'}</h1>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {zoomNotice && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 border ${
          zoomNotice.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}>
          <AlertCircle size={16} className="shrink-0" /> {zoomNotice.text}
        </div>
      )}

      {/* ── Course Type Toggle ───────────────────────────────── */}
      <Card title="Course type">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <TypeButton
            active={form.course_type === 'async'}
            icon={Monitor}
            label="Asynchronous"
            sublabel="Self-paced course"
            onClick={() => updateField('course_type', 'async')}
          />
          <TypeButton
            active={form.course_type === 'live'}
            icon={Radio}
            label="Live"
            sublabel="Scheduled live session"
            onClick={() => updateField('course_type', 'live')}
          />
          <TypeButton
            active={isHybrid}
            icon={Users2}
            label="Hybrid"
            sublabel="In-person + Zoom event"
            onClick={() => updateField('course_type', 'hybrid')}
          />
          <TypeButton
            active={isTutorial}
            icon={Video}
            label="Tutorial"
            sublabel="Video tutorial"
            onClick={() => updateField('course_type', 'tutorial')}
          />
        </div>
      </Card>

      {/* ── Live-only fields ─────────────────────────────────── */}
      {isLive && (
        <Card title="Live session details">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Session date & time (Eastern)">
              <DateTimePicker
                value={form.live_date_utc}
                onChange={(v) => {
                  updateField('live_date_utc', v);
                  if (v) {
                    const [datePart] = v.split('T');
                    const [y, m, d] = datePart.split('-').map(Number);
                    updateField('live_date', new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
                  } else {
                    updateField('live_date', '');
                  }
                }}
              />
            </Field>
            <Field label="Runtime (minutes)" hint="Length of the live session — auto-fills the minimum attendance">
              <input
                type="number"
                min={0}
                value={form.duration_minutes}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => {
                    const next = { ...f, duration_minutes: v };
                    // Mirror runtime into minimum attendance until it's overridden.
                    if (!minAttendanceTouched.current) next.min_attendance_minutes = v;
                    return next;
                  });
                }}
                placeholder="90"
                className="input"
              />
            </Field>
          </div>
        </Card>
      )}

      {/* ── Core Details ─────────────────────────────────────── */}
      <Card title="Course details">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Title" required className="sm:col-span-2">
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Short description" hint="Shown on catalog cards (~160 chars)" className="sm:col-span-2">
            <textarea
              rows={2}
              maxLength={300}
              value={form.short_description}
              onChange={(e) => updateField('short_description', e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Price" hint="In dollars (e.g. 99.00 — enter 0 for free)">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={form.price_dollars}
                onChange={(e) => updateField('price_dollars', e.target.value)}
                onBlur={(e) => {
                  const n = parseFloat(e.target.value);
                  updateField('price_dollars', Number.isFinite(n) && n > 0 ? n.toFixed(2) : '');
                }}
                placeholder="0.00"
                className="input pl-7"
              />
            </div>
          </Field>
          {!isLive && (
            <Field label="Duration (minutes)" hint="Estimated completion time">
              <input
                type="number"
                min={0}
                value={form.duration_minutes}
                onChange={(e) => updateField('duration_minutes', e.target.value)}
                className="input"
              />
            </Field>
          )}
          <div className="sm:col-span-2 grid sm:grid-cols-3 gap-4">
            <Field label="CEU hours" hint="Continuing education units (shown on certificate)">
              <input
                type="number"
                min={0}
                step="0.5"
                value={form.ceu_hours}
                onChange={(e) => updateField('ceu_hours', e.target.value)}
                placeholder="e.g. 3.0"
                className="input"
              />
            </Field>
            <Field label="LPCA approval #" hint="Licensed Professional Counselors Association of Georgia. Leave blank if not approved.">
              <input
                type="text"
                maxLength={60}
                value={form.lpca_approval_number}
                onChange={(e) => updateField('lpca_approval_number', e.target.value)}
                placeholder="e.g. S-0926-1156"
                className="input font-mono"
              />
            </Field>
            <Field label="GACA approval #" hint="Georgia Addiction Counselors Association. Leave blank if not approved.">
              <input
                type="text"
                maxLength={60}
                value={form.gaca_approval_number}
                onChange={(e) => updateField('gaca_approval_number', e.target.value)}
                placeholder="e.g. 2026-000-755"
                className="input font-mono"
              />
            </Field>
          </div>
          <Field label="Long description" hint="Markdown-friendly. Shown on detail page." className="sm:col-span-2">
            <textarea
              rows={8}
              value={form.long_description_md}
              onChange={(e) => updateField('long_description_md', e.target.value)}
              className="input font-mono text-xs"
            />
          </Field>
        </div>
      </Card>

      {/* ── What You'll Learn ────────────────────────────────── */}
      <Card title="What you'll learn">
        <p className="text-sm text-slate-500 mb-4">Bullet points shown on the course card/detail page.</p>
        <BulletEditor
          items={form.what_youll_learn}
          onChange={(items) => updateField('what_youll_learn', items)}
        />
      </Card>

      {/* ── Learning Objectives (for certificate) ──────────── */}
      <Card title="Learning objectives" icon={BookOpen}>
        <p className="text-sm text-slate-500 mb-4">Printed on the certificate. Each item becomes a bullet point.</p>
        <BulletEditor
          items={form.learning_objectives}
          onChange={(items) => updateField('learning_objectives', items)}
          placeholder="e.g. Identify the pharmacological effects and risks of THC"
        />
      </Card>

      {/* ── Hosted By (Live only) ────────────────────────────── */}
      {isLive && (
        <Card title="Hosted by">
          <div className="space-y-4">
            <div className="flex gap-4 items-start">
              <div className="shrink-0">
                {(pendingPresenterImg || course?.hosted_by_image_url) ? (
                  <div className="relative">
                    <img
                      src={pendingPresenterImg ? URL.createObjectURL(pendingPresenterImg) : course.hosted_by_image_url}
                      alt="Presenter"
                      className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                    />
                    {pendingPresenterImg && (
                      <button
                        type="button"
                        onClick={() => { setPendingPresenterImg(null); if (presenterImgRef.current) presenterImgRef.current.value = ''; }}
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center text-slate-500 hover:text-rose-500"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                    <User size={28} />
                  </div>
                )}
                <label className="relative overflow-hidden block mt-2 text-center">
                  <span className="text-xs text-brand font-medium cursor-pointer hover:text-brand-dark">
                    {pendingPresenterImg || course?.hosted_by_image_url ? 'Change' : 'Add photo'}
                  </span>
                  <input
                    ref={presenterImgRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => setPendingPresenterImg(e.target.files?.[0] || null)}
                    className="sr-only"
                  />
                </label>
              </div>
              <div className="flex-1 grid sm:grid-cols-2 gap-4">
                <Field label="Name" hint="e.g. Dr. Lena Marquez">
                  <input
                    type="text"
                    value={form.hosted_by}
                    onChange={(e) => updateField('hosted_by', e.target.value)}
                    placeholder="Dr. Lena Marquez"
                    className="input"
                  />
                </Field>
                <Field label="Title / Credentials" hint="e.g. LCC, LCSW">
                  <input
                    type="text"
                    value={form.hosted_by_title}
                    onChange={(e) => updateField('hosted_by_title', e.target.value)}
                    placeholder="Licensed Clinical Counselor"
                    className="input"
                  />
                </Field>
              </div>
            </div>
            <Field label="Short bio" hint="Shown as a dropdown on the course detail card">
              <textarea
                rows={6}
                value={form.hosted_by_bio}
                onChange={(e) => updateField('hosted_by_bio', e.target.value)}
                placeholder="A brief professional bio for the presenter…"
                className="input"
              />
            </Field>
          </div>
        </Card>
      )}

      {/* ── Venue & check-in (Hybrid only) ─────────────────────── */}
      {isHybrid && (
        <Card title="Venue & in-person check-in" icon={MapPin}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Venue name">
              <input
                type="text"
                value={form.venue_name}
                onChange={(e) => updateField('venue_name', e.target.value)}
                placeholder="ADE Training Center"
                className="input w-full"
              />
            </Field>
            <Field label="Venue address">
              <input
                type="text"
                value={form.venue_address}
                onChange={(e) => updateField('venue_address', e.target.value)}
                placeholder="123 Main St, Atlanta, GA"
                className="input w-full"
              />
            </Field>
            <Field
              label="In-person capacity"
              hint="Seats in the room. Leave blank for no limit. Only limits in-person registrations — online seats are unlimited."
            >
              <input
                type="number"
                min="1"
                value={form.in_person_capacity}
                onChange={(e) => updateField('in_person_capacity', e.target.value)}
                placeholder="No limit"
                className="input w-full max-w-[10rem]"
              />
            </Field>
            <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.lunch_included}
                onChange={(e) => updateField('lunch_included', e.target.checked)}
                className="rounded border-slate-300"
              />
              Lunch included for in-person attendees
              <span className="text-xs text-slate-400">Shown next to "Certificate on completion" on the course page and featured card.</span>
            </label>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            In-person attendees never appear in the Zoom attendance report, so checking in is
            what unlocks their evaluation and certificate. Set up the check-in windows below;
            anyone who can't check in can be checked in by an admin from the course roster.
          </p>
        </Card>
      )}

      {/* ── In-person check-in windows (Hybrid only) ───────────── */}
      {isHybrid && <CheckinWindowsCard courseId={id} isNew={isNew} course={course} />}

      {/* ── Conference check-in page (Hybrid only) ─────────────── */}
      {isHybrid && (
        <ConferenceCheckinCard
          form={form} updateField={updateField} course={course} setCourse={setCourse} isNew={isNew}
        />
      )}

      {/* ── Event agenda (Live + Hybrid) ───────────────────────── */}
      {isLive && (
        <AgendaCard courseId={id} isNew={isNew} pendingAgenda={pendingAgenda} setPendingAgenda={setPendingAgenda} />
      )}

      {/* ── Roadmap Content (Live only) ────────────────────────── */}
      {isLive && (
        <Card title="Roadmap content" icon={BookOpen}>
          <p className="text-sm text-slate-500 mb-4">
            Shown to students before the live session. Each section heading is styled
            automatically in the brand color — just enter the content. Start a line with
            <code className="mx-1 px-1 rounded bg-slate-100 text-slate-700">-</code> for a
            bullet, or <code className="mx-1 px-1 rounded bg-slate-100 text-slate-700">1.</code>
            for a numbered step.
          </p>
          <div className="space-y-4">
            <Field label="Description" hint="Overview of the course and what to expect.">
              <textarea
                rows={5}
                value={form.live_roadmap_description}
                onChange={(e) => updateField('live_roadmap_description', e.target.value)}
                placeholder="Welcome to this course. Here's what to expect…"
                className="input text-sm w-full"
              />
            </Field>
            <Field label="Objectives" hint="What students will be able to do — one per line, starting with -">
              <textarea
                rows={5}
                value={form.live_roadmap_objectives}
                onChange={(e) => updateField('live_roadmap_objectives', e.target.value)}
                placeholder={'- Objective one\n- Objective two'}
                className="input text-sm w-full"
              />
            </Field>
            <Field label="Course Flow" hint="The agenda or sequence of the session.">
              <textarea
                rows={5}
                value={form.live_roadmap_flow}
                onChange={(e) => updateField('live_roadmap_flow', e.target.value)}
                placeholder={'1. Welcome & introductions\n2. Core content\n3. Q&A'}
                className="input text-sm w-full"
              />
            </Field>
          </div>
          {form.live_roadmap_md
            && !form.live_roadmap_description
            && !form.live_roadmap_objectives
            && !form.live_roadmap_flow && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">Previous roadmap content (for reference)</p>
              <p className="text-xs text-amber-700 mb-2">
                Copy what you need into the sections above. This older content still shows to
                students until the sections are filled in.
              </p>
              <pre className="text-xs text-amber-900 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">{form.live_roadmap_md}</pre>
            </div>
          )}
        </Card>
      )}

      {/* ── Zoom Integration (Live only) ─────────────────────── */}
      {isLive && (
        <Card title="Zoom integration" icon={LinkIcon}>
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Step 1: Session title */}
            <Field label="Session title" hint="Title shown in Zoom (defaults to course title)" className="sm:col-span-2">
              <input
                type="text"
                value={form.zoom_session_title || ''}
                onChange={(e) => updateField('zoom_session_title', e.target.value)}
                placeholder={form.title || 'e.g. Motivational Interviewing — May 2026'}
                className="input"
              />
            </Field>

            {/* Step 2: Date & attendance */}
            <Field label="Session date & time (Eastern)" hint="Used for Zoom scheduling and attendance sync">
              <DateTimePicker
                value={form.live_date_utc}
                onChange={(v) => {
                  updateField('live_date_utc', v);
                  if (v) {
                    const [datePart] = v.split('T');
                    const [y, m, d] = datePart.split('-').map(Number);
                    updateField('live_date', new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
                  } else {
                    updateField('live_date', '');
                  }
                }}
              />
            </Field>
            <Field label="Minimum attendance (minutes)" hint="Defaults to the runtime; students must attend at least this long to unlock the evaluation">
              <input
                type="number"
                min={0}
                value={form.min_attendance_minutes}
                onChange={(e) => {
                  minAttendanceTouched.current = true;
                  updateField('min_attendance_minutes', e.target.value);
                }}
                placeholder="30"
                className="input"
              />
            </Field>

            {/* Step 3: Create Session → auto-populates Meeting ID */}
            <div className="sm:col-span-2">
              {form.zoom_meeting_id ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Zoom Meeting ID">
                    <div className="flex items-center gap-2">
                      <input type="text" value={form.zoom_meeting_id} readOnly className="input font-mono bg-green-50 border-green-200 flex-1" />
                      <button
                        type="button"
                        disabled={deletingZoom}
                        onClick={async () => {
                          if (!confirm('Delete this Zoom meeting? This will remove it from the Zoom account.')) return;
                          setDeletingZoom(true);
                          try {
                            await api(`/api/admin/zoom-meeting/${form.zoom_meeting_id}`, { method: 'DELETE' });
                          } catch (err) {
                            console.warn('Zoom delete failed (clearing locally anyway):', err.message);
                          }
                          updateField('zoom_meeting_id', '');
                          updateField('zoom_join_url', '');
                          setDeletingZoom(false);
                        }}
                        className="text-xs text-slate-500 hover:text-rose-600 shrink-0"
                      >
                        {deletingZoom ? 'Deleting…' : 'Clear'}
                      </button>
                    </div>
                  </Field>
                  {form.zoom_join_url && (
                    <Field label="Join URL">
                      <a href={form.zoom_join_url} target="_blank" rel="noreferrer" className="text-sm text-brand hover:underline break-all mt-2 inline-block">
                        {form.zoom_join_url}
                      </a>
                    </Field>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm text-slate-700 mb-1">
                    <input
                      type="checkbox"
                      checked={form.zoom_auto_recording === 'cloud'}
                      onChange={(e) => updateField('zoom_auto_recording', e.target.checked ? 'cloud' : 'none')}
                      className="rounded border-slate-300"
                    />
                    Auto-record to cloud
                  </label>
                  <button
                    type="button"
                    disabled={creatingZoom || !(form.zoom_session_title || form.title)}
                    onClick={async () => {
                      setCreatingZoom(true);
                      setZoomError(null);
                      try {
                        const result = await api('/api/admin/create-zoom-meeting', {
                          method: 'POST',
                          body: {
                            topic: form.zoom_session_title || form.title,
                            // Send the Eastern wall-clock in Zoom's LOCAL format
                            // ("yyyy-MM-ddTHH:mm:ss", no Z). createMeeting sends
                            // timezone: 'America/New_York', so Zoom schedules it at this
                            // exact local time. Sending a GMT/Z time alongside a timezone
                            // makes Zoom read the UTC digits as local and shift it (+4/5h).
                            // The seconds are required or Zoom falls back to an instant meeting.
                            startTime: form.live_date_utc ? `${form.live_date_utc}:00` : undefined,
                            durationMinutes: form.duration_minutes ? parseInt(form.duration_minutes, 10) : undefined,
                            autoRecording: form.zoom_auto_recording || 'none',
                          },
                        });
                        updateField('zoom_meeting_id', result.meetingId);
                        updateField('zoom_join_url', result.joinUrl);
                      } catch (err) {
                        setZoomError(err.body?.detail || err.message);
                      } finally {
                        setCreatingZoom(false);
                      }
                    }}
                    className="inline-flex items-center gap-2 bg-[#2D8CFF] text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-[#2681EB] disabled:opacity-50"
                  >
                    <Video size={16} /> {creatingZoom ? 'Creating…' : 'Create Session'}
                  </button>
                  {zoomError && (
                    <div className="px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded text-sm flex items-center gap-2">
                      <AlertCircle size={14} /> {zoomError}
                    </div>
                  )}
                  <div className="border-t border-slate-100 pt-3">
                    <Field label="Or enter a Meeting ID manually" hint="If you already created the meeting in Zoom">
                      <input
                        type="text"
                        value={form.zoom_meeting_id}
                        onChange={(e) => updateField('zoom_meeting_id', e.target.value.replace(/\s/g, ''))}
                        placeholder="85012345678"
                        className="input font-mono"
                      />
                    </Field>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ── Post-Session Evaluation (Live only) ──────────────── */}
      {isLive && (
        <Card title="Post-session evaluation" icon={ClipboardList}>
          <p className="text-sm text-slate-500 mb-4">
            Build the evaluation form students complete after the live session. Responses are stored for CEU reporting.
          </p>
          <EvaluationBuilder
            questions={form.evaluation_questions}
            onChange={(q) => updateField('evaluation_questions', q)}
          />
        </Card>
      )}

      {/* ── Self-paced content source ─────────────────────────── */}
      {form.course_type === 'async' && (
        <EngineContentCard courseId={id} isNew={isNew} form={form} updateField={updateField} course={course} setCourse={setCourse} />
      )}

      {/* ── Course Assets ────────────────────────────────────── */}
      <CourseAssetsCard
        course={course}
        setCourse={setCourse}
        form={form}
        updateField={updateField}
        isNew={isNew}
        isTutorial={isTutorial}
        pendingHero={pendingHero} setPendingHero={setPendingHero} heroRef={heroRef}
        pendingFeaturedImg={pendingFeaturedImg} setPendingFeaturedImg={setPendingFeaturedImg} featuredImgRef={featuredImgRef}
        pendingSocialImg={pendingSocialImg} setPendingSocialImg={setPendingSocialImg} socialImgRef={socialImgRef}
        pendingScorm={pendingScorm} setPendingScorm={setPendingScorm} scormRef={scormRef}
        pendingCert={pendingCert} setPendingCert={setPendingCert} certRef={certRef}
        pendingVideo={pendingVideo} setPendingVideo={setPendingVideo} videoRef={videoRef}
        pendingResource={pendingResource} setPendingResource={setPendingResource} resourceRef={resourceRef}
      />

      {/* ── Drip Content ───────────────────────────────────────── */}
      <DripContentCard courseId={id} isNew={isNew} pendingDripFiles={pendingDripFiles} setPendingDripFiles={setPendingDripFiles} />

      {/* ── Promotional materials ──────────────────────────────── */}
      <PromoMaterialsCard
        courseId={id}
        isNew={isNew}
        pendingPromoFiles={pendingPromoFiles}
        setPendingPromoFiles={setPendingPromoFiles}
      />

      {/* ── Settings ─────────────────────────────────────────── */}
      <Card title="Settings">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Slug" hint="URL-friendly identifier">
            <input
              type="text"
              value={form.slug}
              onChange={(e) => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="input font-mono"
            />
          </Field>
          <Field label="Status">
            <select
              value={form.status}
              onChange={(e) => updateField('status', e.target.value)}
              className="input"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
          <Field label="Display order" hint="Lower = earlier in catalog">
            <input
              type="number"
              value={form.display_order}
              onChange={(e) => updateField('display_order', e.target.value)}
              className="input"
            />
          </Field>
          <div className="sm:col-span-3 flex items-center gap-2 pt-1">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => updateField('is_featured', e.target.checked)}
                className="rounded border-slate-300"
              />
              Show on homepage hero rail
            </label>
          </div>
        </div>
      </Card>

      {/* ── Save Button ──────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-4 mb-8">
        {saveStatus && (
          <span className="text-sm text-slate-500 animate-pulse">{saveStatus}</span>
        )}
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-brand text-white px-5 py-2.5 rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50"
        >
          <Save size={16} /> {saving ? 'Saving…' : isNew ? 'Create course' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────

async function uploadFile(endpoint, fieldName, file) {
  const fd = new FormData();
  fd.append(fieldName, file);
  return apiUpload(endpoint, fd);
}

// ── Reusable bits ────────────────────────────────────────

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 mb-5">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        {Icon && <Icon size={16} className="text-slate-400" />}
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, hint, required, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function TypeButton({ active, icon: Icon, label, sublabel, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 flex items-center gap-3 p-4 rounded-lg border-2 transition-all text-left ${
        active
          ? 'border-brand bg-brand/5 shadow-sm'
          : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        active ? 'bg-brand text-white' : 'bg-slate-100 text-slate-500'
      }`}>
        <Icon size={20} />
      </div>
      <div>
        <div className={`font-semibold ${active ? 'text-brand' : 'text-slate-700'}`}>{label}</div>
        <div className="text-xs text-slate-500">{sublabel}</div>
      </div>
    </button>
  );
}

// ── Inline File Picker ──────────────────────────────────


const FilePicker = forwardRef(function FilePicker(
  { icon: Icon, label, hint, accept, file, onPick, currentValue },
  ref,
) {
  return (
    <div className="flex items-center gap-4">
      {Icon && (
        <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
          <Icon size={20} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="relative overflow-hidden inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm px-3 py-1.5 rounded-md font-medium cursor-pointer transition-colors">
            <Upload size={14} />
            {label}
            <input
              ref={ref}
              type="file"
              accept={accept}
              onChange={(e) => onPick(e.target.files?.[0] || null)}
              className="sr-only"
            />
          </label>
          {file && (
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <span className="truncate max-w-[200px]">{file.name}</span>
              <button
                type="button"
                onClick={() => { onPick(null); if (ref?.current) ref.current.value = ''; }}
                className="text-slate-400 hover:text-rose-500"
                title="Remove"
              >
                <X size={14} />
              </button>
            </div>
          )}
          {!file && currentValue && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <CheckCircle size={12} className="text-emerald-500" /> {currentValue}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1">{hint}</p>
      </div>
    </div>
  );
});

// ── Evaluation Question Builder ─────────────────────────

function EvaluationBuilder({ questions, onChange }) {
  const addQuestion = () =>
    onChange([...questions, { id: crypto.randomUUID(), type: 'likert', prompt: '', required: true }]);
  const updateQuestion = (i, updates) => {
    const next = [...questions];
    next[i] = { ...next[i], ...updates };
    if (updates.type === 'multi_select' && !next[i].options) {
      next[i].options = [''];
    }
    if (updates.type === 'multi_select_text') {
      if (!next[i].options) next[i].options = ['GACA', 'LPCA', 'NBCC'];
      if (next[i].text_label === undefined) next[i].text_label = 'Certification number';
    }
    if (updates.type === 'yes_no' && next[i].explain_label === undefined) {
      next[i].explain_label = 'If yes, please explain';
    }
    onChange(next);
  };
  const removeQuestion = (i) => onChange(questions.filter((_, idx) => idx !== i));
  const moveQuestion = (i, dir) => {
    const next = [...questions];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {questions.map((q, i) => (
        <div key={q.id} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <div className="flex items-start gap-3">
            <span className="text-xs font-bold text-slate-400 mt-2 w-5 text-center shrink-0">{i + 1}</span>
            <div className="flex-1 space-y-3">
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    value={q.prompt}
                    onChange={(e) => updateQuestion(i, { prompt: e.target.value })}
                    placeholder="Enter question text…"
                    className="input w-full"
                  />
                </div>
                <select
                  value={q.type}
                  onChange={(e) => updateQuestion(i, { type: e.target.value })}
                  className="input w-40 shrink-0"
                >
                  <option value="likert">Likert (1–5)</option>
                  <option value="open_ended">Open-ended</option>
                  <option value="multi_select">Multi-select</option>
                  <option value="multi_select_text">Multi-select + text field</option>
                  <option value="yes_no">Yes / No + explanation</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={q.required}
                    onChange={(e) => updateQuestion(i, { required: e.target.checked })}
                  />
                  Required
                </label>
                {q.type === 'multi_select' && (
                  <span className="text-xs text-slate-400">Learner can check multiple options</span>
                )}
                {q.type === 'multi_select_text' && (
                  <span className="text-xs text-slate-400">Each checked option reveals an optional text field (e.g. certification number)</span>
                )}
                {q.type === 'yes_no' && (
                  <span className="text-xs text-slate-400">Yes / No with an explanation field</span>
                )}
              </div>
              {(q.type === 'multi_select' || q.type === 'multi_select_text') && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500">Options</label>
                  {(q.options || ['']).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => {
                          const opts = [...(q.options || [''])];
                          opts[oi] = e.target.value;
                          updateQuestion(i, { options: opts });
                        }}
                        placeholder={`Option ${oi + 1}`}
                        className="input flex-1"
                      />
                      {(q.options || ['']).length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const opts = (q.options || ['']).filter((_, idx) => idx !== oi);
                            updateQuestion(i, { options: opts });
                          }}
                          className="p-1 text-slate-400 hover:text-rose-500 transition-colors"
                          title="Remove option"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center gap-4 pt-1">
                    <button
                      type="button"
                      onClick={() => updateQuestion(i, { options: [...(q.options || ['']), ''] })}
                      className="text-xs text-brand hover:text-brand-dark font-medium"
                    >
                      + Add option
                    </button>
                    {q.type === 'multi_select' && (
                      <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={!!q.allow_other}
                          onChange={(e) => updateQuestion(i, { allow_other: e.target.checked })}
                          className="rounded border-slate-300"
                        />
                        Include "Other" with text input
                      </label>
                    )}
                  </div>
                  {q.type === 'multi_select_text' && (
                    <div className="pt-1">
                      <label className="text-xs font-medium text-slate-500">Text field label (optional, never required)</label>
                      <input
                        type="text"
                        value={q.text_label ?? 'Certification number'}
                        onChange={(e) => updateQuestion(i, { text_label: e.target.value })}
                        placeholder="Certification number"
                        className="input w-full mt-1"
                      />
                    </div>
                  )}
                </div>
              )}
              {q.type === 'yes_no' && (
                <div>
                  <label className="text-xs font-medium text-slate-500">Explanation prompt</label>
                  <input
                    type="text"
                    value={q.explain_label ?? 'If yes, please explain'}
                    onChange={(e) => updateQuestion(i, { explain_label: e.target.value })}
                    placeholder="If yes, please explain"
                    className="input w-full mt-1"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-0.5 shrink-0">
              <button
                type="button"
                onClick={() => moveQuestion(i, -1)}
                disabled={i === 0}
                className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                title="Move up"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveQuestion(i, 1)}
                disabled={i === questions.length - 1}
                className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                title="Move down"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => removeQuestion(i)}
                className="p-1 text-slate-400 hover:text-rose-500 transition-colors"
                title="Remove question"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addQuestion}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-dark transition-colors mt-2"
      >
        <Plus size={14} /> Add question
      </button>
    </div>
  );
}

// ── Bullet Point Editor ─────────────────────────────────

function BulletEditor({ items, onChange, placeholder }) {
  const addBullet = () => onChange([...items, '']);
  const updateBullet = (i, value) => {
    const next = [...items];
    next[i] = value;
    onChange(next);
  };
  const removeBullet = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-brand font-bold text-sm">•</span>
          <input
            type="text"
            value={item}
            onChange={(e) => updateBullet(i, e.target.value)}
            placeholder={placeholder || "e.g. Core MI principles and techniques"}
            className="input flex-1"
          />
          <button
            type="button"
            onClick={() => removeBullet(i)}
            className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors"
            title="Remove bullet"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addBullet}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-dark transition-colors mt-2"
      >
        <Plus size={14} /> Add bullet point
      </button>
    </div>
  );
}

// ── Course Assets Card ─────────────────────────────────

function CourseAssetsCard({
  course, setCourse, form, updateField, isNew, isTutorial,
  pendingHero, setPendingHero, heroRef,
  pendingFeaturedImg, setPendingFeaturedImg, featuredImgRef,
  pendingSocialImg, setPendingSocialImg, socialImgRef,
  pendingScorm, setPendingScorm, scormRef,
  pendingCert, setPendingCert, certRef,
  pendingVideo, setPendingVideo, videoRef,
  pendingResource, setPendingResource, resourceRef,
}) {
  const [deleting, setDeleting] = useState(null);

  const deleteAsset = async (assetType) => {
    if (!course?.id) return;
    if (!confirm(`Delete this asset? The file will be permanently removed.`)) return;
    setDeleting(assetType);
    try {
      await api(`/api/admin/courses/${course.id}/assets/${assetType}`, { method: 'DELETE' });
      const clearMap = {
        hero_image: { hero_image_url: null },
        featured_image: { featured_image_url: null },
        social_image: { social_image_url: null },
        scorm: { scorm_folder_name: null, scorm_entry_point: null },
        cert_template: { cert_template_url: null },
        video: { video_url: null },
        student_resource: { student_resource_url: null },
        presenter_image: { hosted_by_image_url: null },
      };
      setCourse((c) => ({ ...c, ...clearMap[assetType] }));
    } catch (err) {
      alert(err.body?.error || err.message);
    } finally {
      setDeleting(null);
    }
  };

  const assets = [
    {
      key: 'hero_image', label: 'Course card image', icon: ImageIcon,
      uploaded: !!course?.hero_image_url, detail: course?.hero_image_url ? 'Image uploaded' : null,
      pending: pendingHero, setPending: setPendingHero, ref: heroRef,
      accept: 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml',
      hint: '1920×1080 (16:9), JPG or PNG. Auto-resized on upload.',
      preview: pendingHero ? URL.createObjectURL(pendingHero) : course?.hero_image_url,
      show: true,
    },
    {
      key: 'featured_image', label: 'Featured card image', icon: ImageIcon,
      uploaded: !!course?.featured_image_url, detail: course?.featured_image_url ? 'Image uploaded' : null,
      pending: pendingFeaturedImg, setPending: setPendingFeaturedImg, ref: featuredImgRef,
      accept: 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml',
      hint: '1000×800 (5:4), JPG or PNG. Homepage featured card.',
      preview: pendingFeaturedImg ? URL.createObjectURL(pendingFeaturedImg) : course?.featured_image_url,
      show: true,
    },
    {
      key: 'social_image', label: 'Social share image', icon: ImageIcon,
      uploaded: !!course?.social_image_url, detail: course?.social_image_url ? 'Image uploaded' : null,
      pending: pendingSocialImg, setPending: setPendingSocialImg, ref: socialImgRef,
      accept: 'image/png,image/jpeg,image/webp',
      hint: 'Optional. 1200×630, JPG or PNG. Link previews on Facebook, LinkedIn, etc. If empty, the course card image is used with side margins.',
      preview: pendingSocialImg ? URL.createObjectURL(pendingSocialImg) : course?.social_image_url,
      show: true,
    },
    {
      key: 'scorm', label: 'SCORM package', icon: Package,
      uploaded: !!course?.scorm_entry_point,
      detail: course?.scorm_entry_point ? `${course.scorm_folder_name}/${course.scorm_entry_point}` : null,
      pending: pendingScorm, setPending: setPendingScorm, ref: scormRef,
      accept: '.zip,application/zip',
      hint: 'SCORM 1.2 .zip',
      show: form.course_type === 'async' && form.content_format !== 'engine',
    },
    {
      key: 'video', label: 'Tutorial video', icon: Video,
      uploaded: !!course?.video_url, detail: course?.video_url ? 'Video uploaded' : null,
      pending: pendingVideo, setPending: setPendingVideo, ref: videoRef,
      accept: 'video/mp4,video/quicktime,video/webm,video/x-msvideo',
      hint: 'MP4/MOV/WebM up to 2 GB',
      show: isTutorial,
    },
    {
      key: 'cert_template', label: 'Certificate template', icon: FileText,
      uploaded: !!course?.cert_template_url, detail: course?.cert_template_url ? 'Custom template uploaded' : null,
      pending: pendingCert, setPending: setPendingCert, ref: certRef,
      accept: 'application/pdf,image/png,image/jpeg',
      hint: 'PDF or image background',
      show: !isTutorial && !form.use_default_cert_template,
    },
    {
      key: 'student_resource', label: 'Student resource', icon: Download,
      uploaded: !!course?.student_resource_url, detail: course?.student_resource_url ? 'PDF uploaded' : null,
      pending: pendingResource, setPending: setPendingResource, ref: resourceRef,
      accept: 'application/pdf',
      hint: 'PDF for student download',
      show: true,
    },
  ];

  const visible = assets.filter((a) => a.show);
  const uploaded = visible.filter((a) => a.uploaded || a.pending);
  const available = visible.filter((a) => !a.uploaded && !a.pending);

  return (
    <Card title="Course assets" icon={Archive}>
      {/* Certificate template default toggle */}
      {!isTutorial && (
        <label className="inline-flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            checked={form.use_default_cert_template}
            onChange={(e) => {
              updateField('use_default_cert_template', e.target.checked);
              if (e.target.checked) setPendingCert(null);
            }}
            className="rounded border-slate-300"
          />
          <span className="text-sm text-slate-700">Use the default Learnable certificate template</span>
        </label>
      )}

      {/* Uploaded assets list */}
      {uploaded.length > 0 && (
        <div className="space-y-2 mb-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Uploaded assets</p>
          {uploaded.map((a) => {
            const Icon = a.icon;
            const isPending = !!a.pending;
            return (
              <div key={a.key} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50 group">
                {a.preview ? (
                  <img src={a.preview} alt={a.label} className="w-10 h-10 rounded-lg object-cover border border-slate-200 shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-slate-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{a.label}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {isPending ? (
                      <span className="text-amber-600 flex items-center gap-1">
                        <Upload size={10} /> {a.pending.name} (pending save)
                      </span>
                    ) : (
                      <span className="text-emerald-600 flex items-center gap-1">
                        <CheckCircle size={10} /> {a.detail}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {/* Replace button */}
                  <label className="relative overflow-hidden inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand font-medium cursor-pointer px-2 py-1 rounded hover:bg-slate-100 transition-colors">
                    <Upload size={12} /> Replace
                    <input
                      ref={a.ref}
                      type="file"
                      accept={a.accept}
                      onChange={(e) => a.setPending(e.target.files?.[0] || null)}
                      className="sr-only"
                    />
                  </label>
                  {/* Delete button */}
                  {isPending ? (
                    <button
                      type="button"
                      onClick={() => { a.setPending(null); if (a.ref?.current) a.ref.current.value = ''; }}
                      className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors"
                      title="Remove pending file"
                    >
                      <X size={14} />
                    </button>
                  ) : !isNew && (
                    <button
                      type="button"
                      onClick={() => deleteAsset(a.key)}
                      disabled={deleting === a.key}
                      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600 font-medium px-2 py-1 rounded hover:bg-rose-50 transition-colors disabled:opacity-50"
                      title="Delete asset"
                    >
                      <Trash2 size={12} /> {deleting === a.key ? 'Deleting…' : 'Delete'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload new assets */}
      {available.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            {uploaded.length > 0 ? 'Add more assets' : 'Upload assets'}
          </p>
          {available.map((a) => (
            <FilePicker
              key={a.key}
              ref={a.ref}
              icon={a.icon}
              label={`Upload ${a.label.toLowerCase()}`}
              hint={a.hint}
              accept={a.accept}
              file={a.pending}
              onPick={a.setPending}
            />
          ))}
        </div>
      )}

      {visible.length === 0 && (
        <p className="text-sm text-slate-400">No asset types available for this course type.</p>
      )}
    </Card>
  );
}

// ── Drip Content Card ──────────────────────────────────

function detectContentType(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  if (ext === 'zip') return 'scorm';
  if (ext === 'pdf') return 'pdf';
  if (['mp4', 'mov', 'avi', 'webm', 'mkv', 'wmv', 'm4v'].includes(ext)) return 'video';
  return 'pdf';
}

function titleFromFilename(name) {
  return name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();
}

// Public, event-branded check-in page (/events/<slug>/checkin) that the window
// QR codes open instead of the logged-in player. Attendees give a name and
// email; scans are credited after the last window closes (email matches
// automatically, the rest reviewed on the Reports page).
function ConferenceCheckinCard({ form, updateField, course, setCourse, isNew }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const savedOn = !!course?.conference_checkin;

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const { brand_logo_url } = await uploadFile(`/api/admin/courses/${course.id}/brand-logo-upload`, 'image', file);
      setCourse((c) => ({ ...c, brand_logo_url }));
    } catch { setError('Could not upload that logo.'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const removeLogo = async () => {
    try {
      await api(`/api/admin/courses/${course.id}`, { method: 'PATCH', body: { brand_logo_url: null } });
      setCourse((c) => ({ ...c, brand_logo_url: null }));
    } catch { setError('Could not remove the logo.'); }
  };

  const colorField = (key, label, hint, fallback) => (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={/^#[0-9a-f]{6}$/i.test(form[key]) ? form[key] : fallback}
          onChange={(e) => updateField(key, e.target.value.toUpperCase())}
          className="h-9 w-12 rounded border border-slate-300 bg-white p-0.5 cursor-pointer"
        />
        <input
          type="text"
          value={form[key]}
          onChange={(e) => updateField(key, e.target.value.trim())}
          placeholder={fallback}
          maxLength={7}
          className="input w-28 font-mono uppercase"
        />
      </div>
    </Field>
  );

  if (isNew) {
    return (
      <Card title="Conference check-in page" icon={Smartphone}>
        <div className="text-sm text-slate-500">Save the course first, then set up a branded check-in page.</div>
      </Card>
    );
  }

  return (
    <Card title="Conference check-in page" icon={Smartphone}>
      <label className="flex items-start gap-2 text-sm text-slate-700 mb-4">
        <input
          type="checkbox"
          checked={form.conference_checkin}
          onChange={(e) => updateField('conference_checkin', e.target.checked)}
          className="rounded border-slate-300 mt-0.5"
        />
        <span>
          <strong>Use a branded check-in page for this event.</strong>
          <span className="block text-xs text-slate-500 mt-0.5">
            The check-in window QR codes open a phone-sized page where attendees enter their name and
            email (no login). Scans are matched to the registration list after the last window closes:
            email matches are credited automatically, everything else waits for review on the Reports page.
            Reprint the QR signage after turning this on.
          </span>
        </span>
      </label>

      {form.conference_checkin && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Event brand name" hint="Shown at the top of the page when there's no logo, and in the browser tab." className="sm:col-span-2">
            <input
              type="text"
              value={form.brand_name}
              onChange={(e) => updateField('brand_name', e.target.value)}
              placeholder="GRMP Summit"
              maxLength={80}
              className="input w-full max-w-sm"
            />
          </Field>

          <Field label="Logo" hint="PNG or JPG, wide format works best. Shown at the top of the page." className="sm:col-span-2">
            <div className="flex items-center gap-3 flex-wrap">
              {course?.brand_logo_url && (
                <img src={course.brand_logo_url} alt="" className="h-14 w-auto max-w-[16rem] object-contain rounded border border-slate-200 bg-white p-1" />
              )}
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={(e) => uploadLogo(e.target.files?.[0])} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="text-sm px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
                <Upload size={14} /> {uploading ? 'Uploading…' : course?.brand_logo_url ? 'Replace' : 'Upload logo'}
              </button>
              {course?.brand_logo_url && (
                <button type="button" onClick={removeLogo} className="text-sm text-rose-600 hover:text-rose-800">Remove</button>
              )}
            </div>
          </Field>

          {colorField('brand_color', 'Headline color', 'Top stripe and event title.', '#F7AC1A')}
          {colorField('brand_accent_color', 'Button color', 'Buttons and the confirmation check. Text on it turns white or dark automatically.', '#334155')}

          {error && <div className="sm:col-span-2 text-xs text-rose-600">{error}</div>}

          {course?.slug && (
            <div className="sm:col-span-2 text-sm">
              {savedOn ? (
                <a href={`/events/${course.slug}/checkin`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800">
                  <ExternalLink size={14} /> Open the check-in page
                </a>
              ) : (
                <span className="text-slate-500">Save the course to turn the page on.</span>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// Segments of the day an in-person attendee must check into. One check-in
// proves arrival; requiring one per window is what evidences presence either
// side of a lunch break — the thing a CEU audit asks about.
//
// Each window gets its own code and QR. The QR is a convenience layer over the
// same code, not a stronger proof: print both so an attendee without a working
// camera can still type it.
function CheckinWindowsCard({ courseId, isNew, course }) {
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const blank = { label: '', opens_at: '', closes_at: '', required: true };
  const [draft, setDraft] = useState(blank);

  const load = () => {
    if (isNew) return;
    setLoading(true);
    api(`/api/admin/courses/${courseId}/checkin-windows`)
      .then((d) => setWindows(d.checkin_windows || []))
      .catch(() => setError('Could not load check-in windows.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [courseId, isNew]);

  const reset = () => { setDraft(blank); setEditingId(null); };

  const submit = async () => {
    if (!draft.label.trim()) { setError('Give the window a name.'); return; }
    if (!draft.opens_at || !draft.closes_at) { setError('Set when the window opens and closes.'); return; }
    setBusy(true); setError(null);
    const body = {
      label: draft.label.trim(),
      opens_at: easternToUtc(draft.opens_at),
      closes_at: easternToUtc(draft.closes_at),
      required: draft.required,
    };
    try {
      if (editingId) await api(`/api/admin/checkin-windows/${editingId}`, { method: 'PATCH', body });
      else await api(`/api/admin/courses/${courseId}/checkin-windows`, { method: 'POST', body });
      load(); reset();
    } catch (err) {
      const e = err.body?.error;
      setError(e === 'closes_before_opens' ? 'The window has to close after it opens.'
        : 'Could not save that window.');
    } finally { setBusy(false); }
  };

  const edit = (w) => {
    setEditingId(w.id);
    setDraft({
      label: w.label, required: w.required,
      opens_at: utcToEastern(w.opens_at), closes_at: utcToEastern(w.closes_at),
    });
  };

  const remove = async (w) => {
    if (!confirm(`Delete the "${w.label}" window? Any check-ins recorded against it are removed too.`)) return;
    try {
      await api(`/api/admin/checkin-windows/${w.id}`, { method: 'DELETE' });
      setWindows((prev) => prev.filter((x) => x.id !== w.id));
    } catch { setError('Could not delete that window.'); }
  };

  // Printable signage: one page per window, QR plus the code in large type.
  const printSignage = async () => {
    const QR = (await import('qrcode')).default;
    const origin = window.location.origin;
    const cards = [];
    for (const w of windows) {
      const url = course.conference_checkin
        ? `${origin}/events/${course.slug}/checkin?c=${encodeURIComponent(w.code)}`
        : `${origin}/courses/${course.slug}/live?checkin=${encodeURIComponent(w.code)}`;
      const dataUrl = await QR.toDataURL(url, { width: 900, margin: 1, errorCorrectionLevel: 'M' });
      cards.push({ w, dataUrl, url });
    }
    const win = window.open('', '_blank');
    if (!win) { setError('Allow pop-ups to print the signage.'); return; }
    win.document.write(`<!doctype html><html><head><title>Check-in signage — ${course.title}</title>
      <style>
        @page { size: letter portrait; margin: 0.5in; }
        body { font-family: -apple-system, system-ui, sans-serif; margin: 0; color: #0f172a; }
        .card { page-break-after: always; height: 10in; display: flex; flex-direction: column;
                align-items: center; justify-content: center; text-align: center; }
        .card:last-child { page-break-after: auto; }
        h1 { font-size: 34pt; margin: 0 0 4pt; }
        h2 { font-size: 20pt; font-weight: 600; color: #475569; margin: 0 0 24pt; }
        img { width: 4.6in; height: 4.6in; margin-bottom: 18pt; }
        .hint { font-size: 13pt; color: #475569; max-width: 6in; line-height: 1.5; }
        .times { font-size: 12pt; color: #64748b; margin-top: 14pt; }
      </style></head><body>
      ${cards.map(({ w, dataUrl }) => `
        <div class="card">
          <h1>${w.label}</h1>
          <h2>${/check.?out/i.test(w.label) ? 'Check out' : 'Check in'} for CE credit</h2>
          <img src="${dataUrl}" alt="${w.label} QR code" />
          <p class="hint">${course.conference_checkin
            ? 'Scan with your phone camera, then enter your name and the email you registered with. No login needed. Can’t scan? See the registration table.'
            : 'Scan with your phone camera and sign in to your Learnable account. Can’t scan? See an event admin.'}</p>
          <p class="times">Open ${fmtWindow(w.opens_at)} – ${fmtWindow(w.closes_at)}</p>
        </div>`).join('')}
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const stateStyle = { open: 'bg-emerald-100 text-emerald-700', upcoming: 'bg-slate-100 text-slate-600', closed: 'bg-slate-100 text-slate-400' };

  return (
    <Card title="In-person check-in windows" icon={QrCode}>
      <p className="text-xs text-slate-500 -mt-2 mb-4">
        Segments of the day an in-person attendee must check into. Attendance verifies only once
        every <strong>required</strong> window is covered — that's what shows they were present either
        side of a break. Without windows, in-person attendees can only be checked in by an admin from the roster.
      </p>

      {error && <div className="text-xs text-rose-600 mb-3">{error}</div>}

      {isNew ? (
        <div className="text-sm text-slate-500 mb-4">Save the course first, then add check-in windows.</div>
      ) : loading ? (
        <div className="text-sm text-slate-500 mb-4">Loading…</div>
      ) : windows.length === 0 ? (
        <div className="text-sm text-slate-500 mb-4">
          No windows yet. Add one for each scan (e.g. Check in and Check out), then print the QR signage.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden mb-4">
          {windows.map((w) => (
            <li key={w.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
              <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${stateStyle[w.state] || stateStyle.upcoming}`}>
                {w.state}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-900">{w.label}</span>
                  {!w.required && <span className="text-[10px] uppercase tracking-wide text-slate-400">optional</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {fmtWindow(w.opens_at)} – {fmtWindow(w.closes_at)} · {w.checked_in_count} checked in
                </div>
              </div>
              <button type="button" onClick={() => edit(w)} className="text-xs text-slate-400 hover:text-brand transition">Edit</button>
              <button type="button" onClick={() => remove(w)} className="text-slate-400 hover:text-rose-600 transition" title="Delete">
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!isNew && windows.length > 0 && (
        <button type="button" onClick={printSignage}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition mb-4">
          <QrCode size={13} /> Print QR signage ({windows.length} page{windows.length > 1 ? 's' : ''})
        </button>
      )}

      {!isNew && (
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/60">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
            {editingId ? 'Edit window' : 'Add check-in window'}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name" hint="Shown to attendees and on the printed sign, e.g. Check in or Check out" className="sm:col-span-2">
              <input type="text" value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                placeholder="Check in" className="input w-full" />
            </Field>
            <Field label="Opens (Eastern)">
              <DateTimePicker value={draft.opens_at} onChange={(v) => setDraft((d) => ({ ...d, opens_at: v }))} />
            </Field>
            <Field label="Closes (Eastern)">
              <DateTimePicker value={draft.closes_at} onChange={(v) => setDraft((d) => ({ ...d, closes_at: v }))} />
            </Field>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 mt-3">
            <input type="checkbox" checked={draft.required}
              onChange={(e) => setDraft((d) => ({ ...d, required: e.target.checked }))}
              className="rounded border-slate-300" />
            Required for attendance credit
          </label>
          <div className="flex items-center gap-2 mt-3">
            <button type="button" onClick={submit} disabled={busy}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium bg-brand text-white hover:bg-brand-dark transition disabled:opacity-40">
              <Plus size={14} /> {busy ? 'Saving…' : editingId ? 'Save window' : 'Add window'}
            </button>
            {editingId && (
              <button type="button" onClick={reset}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition">
                <X size={14} /> Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// Windows are stored in UTC and always discussed in Eastern, matching the rest
// of the live-course scheduling.
function fmtWindow(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

const SESSION_TYPES = [
  { value: 'keynote',  label: 'Keynote',  color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'breakout', label: 'Breakout', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  { value: 'session',  label: 'Session',  color: 'bg-slate-100 text-slate-600 border-slate-200' },
  { value: 'break',    label: 'Break',    color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
];
const sessionMeta = (t) => SESSION_TYPES.find((x) => x.value === t) || SESSION_TYPES[2];

// 'none' is for items that are neither (lunch, breaks) and gets no pill.
const CE_CREDITS = [
  { value: 'none',    label: 'None',    color: null },
  { value: 'ceu',     label: 'CEU',     color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'non_ceu', label: 'Non CEU', color: 'bg-slate-100 text-slate-600 border-slate-200' },
];
const ceCreditMeta = (v) => CE_CREDITS.find((x) => x.value === v) || CE_CREDITS[0];

// Mirrors the API's ORDER BY (day, start time, then position) so an edited time
// re-sorts without a reload. Position only breaks ties between same-time items.
const sortAgenda = (rows) => [...rows].sort((a, b) =>
  (a.day_number || 1) - (b.day_number || 1)
  || (a.start_time ? 0 : 1) - (b.start_time ? 0 : 1)
  || (a.start_time || '').localeCompare(b.start_time || '')
  || (a.display_order ?? 0) - (b.display_order ?? 0));

const sameSlot = (a, b) =>
  (a.day_number || 1) === (b.day_number || 1) && (a.start_time || '') === (b.start_time || '');

let speakerKeySeq = 0;
// Form-side speaker. `key` is a stable React key; photo_file / remove_photo stay
// client-only until the item is saved.
const toDraftSpeaker = (s = {}) => ({
  key: `speaker-${++speakerKeySeq}`,
  id: s.id || null,
  name: s.name || '',
  bio: s.bio || '',
  image_url: s.image_url || '',
  photo_file: null,
  remove_photo: false,
});

// Photos go up once the item is saved and each speaker has an id. The API
// returns speakers in the order they were sent, so files line up by index.
async function uploadSpeakerPhotos(savedSpeakers = [], files = []) {
  for (const [i, file] of files.entries()) {
    if (file && savedSpeakers[i]) {
      await uploadFile(`/api/admin/agenda-speakers/${savedSpeakers[i].id}/image-upload`, 'image', file);
    }
  }
}

const photoProblem = (code) => (
  code === 'invalid_file_type' ? 'photos must be PNG, JPEG or WebP images'
  : code === 'file_too_large' ? 'photos must be 10MB or smaller'
  : 'please try again'
);

const BLANK_AGENDA_ITEM = {
  day_number: 1, start_time: '', end_time: '', session_type: 'session', ce_credit: 'none', live_only: false,
  title: '', description: '', location: '', speakers: [],
};

// The published schedule for a hybrid event. Keynotes, breakouts and ordinary
// sessions are one list separated by session_type — breakouts are informational,
// nobody signs up for them. day_number carries multi-day events; a single-day
// event is simply day 1 and the day controls stay out of the way.
function AgendaCard({ courseId, isNew, pendingAgenda, setPendingAgenda }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(BLANK_AGENDA_ITEM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  // `quiet` refreshes without swapping the list for "Loading…".
  const load = (quiet = false) => {
    if (isNew) return;
    if (!quiet) setLoading(true);
    api(`/api/admin/courses/${courseId}/agenda`)
      .then((d) => setItems(d.agenda_items || []))
      .catch(() => setError('Could not load the agenda.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [courseId, isNew]);

  // On a new course there is no id to POST against, so rows are staged and
  // created right after the course is saved.
  const list = isNew ? pendingAgenda : items;

  const resetDraft = () => { setDraft({ ...BLANK_AGENDA_ITEM, day_number: draft.day_number }); setEditingId(null); };

  const addSpeaker = () => setDraft((d) => ({ ...d, speakers: [...d.speakers, toDraftSpeaker()] }));
  const updateSpeaker = (key, patch) => setDraft((d) => ({
    ...d, speakers: d.speakers.map((s) => (s.key === key ? { ...s, ...patch } : s)),
  }));
  const removeSpeaker = (key) => setDraft((d) => ({ ...d, speakers: d.speakers.filter((s) => s.key !== key) }));
  const moveSpeaker = (i, dir) => setDraft((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.speakers.length) return d;
    const next = [...d.speakers];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...d, speakers: next };
  });

  const submit = async () => {
    const title = draft.title.trim();
    if (!title) { setError('Give the session a title.'); return; }
    // Untouched blank speaker rows are dropped; one with a bio or photo but no
    // name can't be saved.
    const speakers = draft.speakers.filter((s) =>
      s.name.trim() || s.bio.trim() || s.photo_file || (s.image_url && !s.remove_photo));
    if (speakers.some((s) => !s.name.trim())) { setError('Give each speaker a name.'); return; }
    setError(null);

    const body = {
      ...draft,
      title,
      speakers: speakers.map((s) => ({ ...(s.id ? { id: s.id } : {}), name: s.name.trim(), bio: s.bio })),
    };
    const photoFiles = speakers.map((s) => s.photo_file || null);

    if (isNew) {
      setPendingAgenda((prev) => [...prev, { ...body, speaker_photo_files: photoFiles }]);
      resetDraft();
      return;
    }

    setBusy(true);
    let saved = null;
    try {
      if (editingId) {
        ({ agenda_item: saved } = await api(`/api/admin/agenda-items/${editingId}`, { method: 'PATCH', body }));
        await uploadSpeakerPhotos(saved.speakers, photoFiles);
        for (const s of speakers) {
          if (s.id && s.remove_photo && !s.photo_file) {
            await api(`/api/admin/agenda-speakers/${s.id}/image`, { method: 'DELETE' });
          }
        }
      } else {
        ({ agenda_item: saved } = await api(`/api/admin/courses/${courseId}/agenda`, { method: 'POST', body }));
        await uploadSpeakerPhotos(saved.speakers, photoFiles);
      }
      resetDraft();
    } catch (err) {
      const code = err.body?.error;
      if (saved) {
        // The item and speakers are saved; only a photo failed. Close the form
        // so a retry edits the saved speakers instead of adding duplicates.
        resetDraft();
        setError(`Saved, but a speaker photo didn't upload (${photoProblem(code)}). Edit the item to try again.`);
      } else {
        setError(
          code === 'title_required' ? 'Give the session a title.'
          : code === 'speaker_name_required' ? 'Give each speaker a name.'
          : code === 'invalid_speakers' ? 'The speakers changed elsewhere — reload the page and try again.'
          : code === 'stale_page' ? 'This page is out of date — reload it and try again.'
          : 'Could not save that item.'
        );
      }
    } finally {
      load(true);
      setBusy(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setError(null);
    setDraft({
      day_number: item.day_number || 1,
      start_time: item.start_time || '', end_time: item.end_time || '',
      session_type: item.session_type || 'session', ce_credit: item.ce_credit || 'none', title: item.title || '',
      live_only: !!item.live_only,
      description: item.description || '', location: item.location || '',
      speakers: (item.speakers || []).map(toDraftSpeaker),
    });
  };

  // Swap an item with its neighbour among items sharing the same day and start
  // time — the only case where position matters, since the list sorts by time.
  const move = async (item, dir) => {
    const slot = list.filter((x) => sameSlot(x, item));
    const i = slot.indexOf(item);
    const j = i + dir;
    if (j < 0 || j >= slot.length) return;

    if (isNew) {
      setPendingAgenda((prev) => {
        const next = [...prev];
        const a = prev.indexOf(slot[i]);
        const b = prev.indexOf(slot[j]);
        [next[a], next[b]] = [next[b], next[a]];
        return next;
      });
      return;
    }

    const reordered = [...slot];
    [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
    setBusy(true);
    try {
      const { agenda_items } = await api(`/api/admin/courses/${courseId}/agenda/reorder`, {
        method: 'POST', body: { ids: reordered.map((x) => x.id) },
      });
      setItems(agenda_items);
    } catch {
      setError('Could not reorder that item.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item, idx) => {
    if (isNew) { setPendingAgenda((prev) => prev.filter((_, i) => i !== idx)); return; }
    if (!confirm(`Remove "${item.title}" from the agenda?`)) return;
    try {
      await api(`/api/admin/agenda-items/${item.id}`, { method: 'DELETE' });
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (item.id === editingId) resetDraft();
    } catch { setError('Could not remove that item.'); }
  };

  // Group by day so a multi-day event reads as days, not one long list.
  const days = [...new Set(list.map((i) => i.day_number || 1))].sort((a, b) => a - b);
  const maxDay = days.length ? Math.max(...days) : 1;

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const inlineFormRef = useRef(null);
  useEffect(() => {
    if (editingId) inlineFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [editingId]);

  // One form, shown either inline under the item being edited or at the bottom
  // of the card for adding — never both, since they share the same draft.
  const renderItemForm = (inline) => (
    <div
      ref={inline ? inlineFormRef : undefined}
      className={`border rounded-lg p-4 ${inline ? 'mt-3 border-brand/30 bg-white shadow-sm' : 'border-slate-200 bg-slate-50/60'}`}
    >
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
        {editingId ? 'Edit item' : 'Add agenda item'}
      </div>
      {inline && error && <div className="text-xs text-rose-600 mb-3">{error}</div>}
      <div className="grid sm:grid-cols-5 gap-3">
        <Field label="Type">
          <select value={draft.session_type} onChange={set('session_type')} className="input w-full">
            {SESSION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Credit">
          <select value={draft.ce_credit} onChange={set('ce_credit')} className="input w-full">
            {CE_CREDITS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Day">
          <input type="number" min="1" value={draft.day_number}
            onChange={(e) => setDraft((d) => ({ ...d, day_number: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
            className="input w-full" />
        </Field>
        <Field label="Start">
          <input type="time" value={draft.start_time} onChange={set('start_time')} className="input w-full" />
        </Field>
        <Field label="End">
          <input type="time" value={draft.end_time} onChange={set('end_time')} className="input w-full" />
        </Field>
        <Field label="Title" className="sm:col-span-3">
          <input type="text" value={draft.title} onChange={set('title')}
            placeholder="Opening keynote" className="input w-full" />
        </Field>
        <Field label="Room / track" className="sm:col-span-2">
          <input type="text" value={draft.location} onChange={set('location')}
            placeholder="Main hall" className="input w-full" />
        </Field>
        <label className="sm:col-span-5 inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
          <input type="checkbox" checked={draft.live_only}
            onChange={(e) => setDraft((d) => ({ ...d, live_only: e.target.checked }))}
            className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand" />
          <span className="font-medium">Live only</span>
          <span className="text-xs text-slate-400">Shows a "Live only" tag on this session in the public agenda.</span>
        </label>
        <Field label="Speakers" className="sm:col-span-5">
          <div className="space-y-2">
            {draft.speakers.map((s, i) => (
              <AgendaSpeakerEditor
                key={s.key}
                speaker={s}
                canMoveUp={i > 0}
                canMoveDown={i < draft.speakers.length - 1}
                onChange={(patch) => updateSpeaker(s.key, patch)}
                onMove={(dir) => moveSpeaker(i, dir)}
                onRemove={() => removeSpeaker(s.key)}
              />
            ))}
            <button type="button" onClick={addSpeaker}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-brand hover:text-brand-dark transition">
              <Plus size={13} /> {draft.speakers.length ? 'Add another speaker' : 'Add speaker'}
            </button>
          </div>
        </Field>
        <Field label="Description" className="sm:col-span-5">
          <textarea rows={inline ? 4 : 2} value={draft.description} onChange={set('description')}
            placeholder="Optional detail shown under the session title." className="input w-full" />
        </Field>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button type="button" onClick={submit} disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium bg-brand text-white hover:bg-brand-dark transition disabled:opacity-40">
          <Plus size={14} /> {busy ? 'Saving…' : editingId ? 'Save item' : 'Add to agenda'}
        </button>
        {editingId && (
          <button type="button" onClick={resetDraft}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition">
            <X size={14} /> Cancel
          </button>
        )}
        {isNew && pendingAgenda.length > 0 && (
          <span className="text-xs text-slate-500">
            {pendingAgenda.length} item{pendingAgenda.length > 1 ? 's' : ''} save when you click <strong>Create course</strong>.
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Card title="Event agenda" icon={CalendarDays}>
      <p className="text-xs text-slate-500 -mt-2 mb-4">
        Keynotes, breakouts and sessions shown to attendees. Breakouts are informational —
        attendees see the schedule but don't sign up for a specific one.
      </p>

      {error && !editingId && <div className="text-xs text-rose-600 mb-3">{error}</div>}

      {/* Existing agenda, grouped by day */}
      {!isNew && loading ? (
        <div className="text-sm text-slate-500 mb-4">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-slate-500 mb-4">No agenda items yet.</div>
      ) : (
        <div className="space-y-4 mb-5">
          {days.map((day) => (
            <div key={day}>
              {maxDay > 1 && (
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                  Day {day}
                </div>
              )}
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {list.filter((i) => (i.day_number || 1) === day).map((item) => {
                  const meta = sessionMeta(item.session_type);
                  const globalIdx = list.indexOf(item);
                  const slot = list.filter((x) => sameSlot(x, item));
                  const slotIdx = slot.indexOf(item);
                  const speakers = item.speakers || [];
                  return (
                    <li key={item.id || `pending-${globalIdx}`}
                      className={`px-3 py-2.5 ${item.id && item.id === editingId ? 'bg-orange-50/50' : 'hover:bg-slate-50'}`}>
                      <div className="flex items-start gap-3">
                      <div className="text-xs text-slate-500 tabular-nums w-32 shrink-0 pt-0.5">
                        {formatAgendaTime(item) || '—'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${meta.color}`}>
                            {meta.label}
                          </span>
                          {ceCreditMeta(item.ce_credit).color && (
                            <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${ceCreditMeta(item.ce_credit).color}`}>
                              {ceCreditMeta(item.ce_credit).label}
                            </span>
                          )}
                          {item.live_only && (
                            <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${LIVE_ONLY_META.pill}`}>
                              {LIVE_ONLY_META.label}
                            </span>
                          )}
                          <span className="text-sm font-medium text-slate-900">{item.title}</span>
                        </div>
                        {(speakers.length > 0 || item.location) && (
                          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-x-3 gap-y-1 flex-wrap">
                            {speakers.map((s, i) => (
                              <span key={s.id || i} className="inline-flex items-center gap-1">
                                {s.image_url
                                  ? <img src={s.image_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                                  : <Mic size={11} />}
                                {s.name}
                                {s.bio && <span className="text-slate-400">· bio</span>}
                              </span>
                            ))}
                            {item.location && <span className="inline-flex items-center gap-1"><MapPin size={11} />{item.location}</span>}
                          </div>
                        )}
                        {item.description && <p className="text-xs text-slate-500 mt-1">{item.description}</p>}
                      </div>
                      {slot.length > 1 && (
                        <div className="flex flex-col shrink-0 -my-1" title="Order among sessions starting at the same time">
                          <button type="button" onClick={() => move(item, -1)} disabled={busy || slotIdx === 0}
                            aria-label="Move up" className="text-slate-400 hover:text-brand transition disabled:opacity-25 disabled:hover:text-slate-400">
                            <ChevronUp size={15} />
                          </button>
                          <button type="button" onClick={() => move(item, 1)} disabled={busy || slotIdx === slot.length - 1}
                            aria-label="Move down" className="text-slate-400 hover:text-brand transition disabled:opacity-25 disabled:hover:text-slate-400">
                            <ChevronDown size={15} />
                          </button>
                        </div>
                      )}
                      {!isNew && (
                        item.id === editingId ? (
                          <span className="text-xs font-medium text-brand shrink-0">Editing</span>
                        ) : (
                          <button type="button" onClick={() => edit(item)}
                            className="text-xs text-slate-400 hover:text-brand transition shrink-0">Edit</button>
                        )
                      )}
                      <button type="button" onClick={() => remove(item, globalIdx)}
                        className="text-slate-400 hover:text-rose-600 transition shrink-0" title="Remove">
                        <Trash2 size={14} />
                      </button>
                      </div>
                      {item.id && item.id === editingId && renderItemForm(true)}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Add form — editing happens inline under the item being edited */}
      {!editingId && renderItemForm(false)}
    </Card>
  );
}

// One speaker in the agenda item form: photo, name and bio, with ordering and
// removal. Photo changes are held on the draft until the item is saved.
function AgendaSpeakerEditor({ speaker, canMoveUp, canMoveDown, onChange, onMove, onRemove }) {
  const fileUrl = useMemo(
    () => (speaker.photo_file ? URL.createObjectURL(speaker.photo_file) : null),
    [speaker.photo_file]
  );
  useEffect(() => () => { if (fileUrl) URL.revokeObjectURL(fileUrl); }, [fileUrl]);
  const preview = fileUrl || (!speaker.remove_photo && speaker.image_url) || null;

  return (
    <div className="flex gap-3 items-start border border-slate-200 rounded-lg bg-white p-3">
      <div className="flex flex-col items-center gap-1 shrink-0 w-16">
        {preview ? (
          <img src={preview} alt="" className="w-14 h-14 rounded-full object-cover border border-slate-200" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
            <User size={20} />
          </div>
        )}
        <label className="relative text-[11px] text-brand font-medium cursor-pointer hover:text-brand-dark">
          {preview ? 'Change' : 'Add photo'}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onChange({ photo_file: file, remove_photo: false });
              e.target.value = '';
            }} />
        </label>
        {preview && (
          <button type="button"
            onClick={() => onChange({ photo_file: null, remove_photo: !!speaker.image_url })}
            className="text-[11px] text-slate-400 hover:text-rose-600 transition">
            Remove
          </button>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <input type="text" value={speaker.name} onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Name and credentials, e.g. Dr. Renee Eady, LPC" className="input w-full" />
        <textarea rows={3} value={speaker.bio} onChange={(e) => onChange({ bio: e.target.value })}
          placeholder="Bio (optional) — shown in a Bio dropdown beside the name." className="input w-full" />
      </div>
      <div className="flex flex-col items-center gap-1 shrink-0">
        {(canMoveUp || canMoveDown) && (
          <>
            <button type="button" onClick={() => onMove(-1)} disabled={!canMoveUp} aria-label="Move speaker up"
              className="text-slate-400 hover:text-brand transition disabled:opacity-25 disabled:hover:text-slate-400">
              <ChevronUp size={15} />
            </button>
            <button type="button" onClick={() => onMove(1)} disabled={!canMoveDown} aria-label="Move speaker down"
              className="text-slate-400 hover:text-brand transition disabled:opacity-25 disabled:hover:text-slate-400">
              <ChevronDown size={15} />
            </button>
          </>
        )}
        <button type="button" onClick={onRemove} title="Remove speaker" aria-label="Remove speaker"
          className="text-slate-400 hover:text-rose-600 transition">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// Marketing collateral for a course — flyers, one-pagers, social graphics.
// Admin-only; nothing here is served to students. On a brand-new course there is
// no id to upload against yet, so files are staged and sent right after the
// course is created (same approach as SCORM/hero/drip uploads).
function PromoMaterialsCard({ courseId, isNew, pendingPromoFiles, setPendingPromoFiles }) {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const fileRef = useRef(null);

  const load = () => {
    if (isNew) return;
    setLoading(true);
    api(`/api/admin/courses/${courseId}/promo-materials`)
      .then((d) => setMaterials(d.promo_materials || []))
      .catch(() => setError('Could not load promotional materials.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [courseId, isNew]);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError(null);

    // No course id yet — stage locally and let the save handler upload them.
    if (isNew) {
      setPendingPromoFiles((prev) => [
        ...prev,
        ...files.map((file) => ({ file, title: file.name })),
      ]);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', file.name);
        await apiUpload(`/api/admin/courses/${courseId}/promo-materials`, fd);
      }
      load();
    } catch (err) {
      setError(err.body?.detail || err.body?.error || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (m) => {
    if (!confirm(`Delete "${m.title}"? This permanently removes the file.`)) return;
    try {
      await api(`/api/admin/promo-materials/${m.id}`, { method: 'DELETE' });
      setMaterials((prev) => prev.filter((x) => x.id !== m.id));
    } catch {
      setError('Could not delete that file.');
    }
  };

  const saveRename = async (m) => {
    const title = renameDraft.trim();
    if (!title || title === m.title) { setRenamingId(null); return; }
    try {
      const { promo_material } = await api(`/api/admin/promo-materials/${m.id}`, {
        method: 'PATCH', body: { title },
      });
      setMaterials((prev) => prev.map((x) => (x.id === m.id ? promo_material : x)));
    } catch {
      setError('Could not rename that file.');
    } finally {
      setRenamingId(null);
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    const kb = Number(bytes) / 1024;
    return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <Card title="Promotional materials" icon={Megaphone}>
      <p className="text-xs text-slate-500 -mt-2 mb-4">
        PDFs or images used to promote this course — flyers, one-pagers, social graphics.
        Admin-only; these are never shown to students.
      </p>

      <label className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-slate-200 rounded-lg py-6 px-4 text-center transition ${uploading ? 'opacity-50' : 'cursor-pointer hover:border-brand/40 hover:bg-brand/5'}`}>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="application/pdf,image/png,image/jpeg,image/gif,image/webp"
          disabled={uploading}
          onChange={(e) => addFiles(e.target.files)}
          className="hidden"
        />
        <Upload size={18} className="text-slate-400" />
        <span className="text-sm font-medium text-slate-700">
          {uploading ? 'Uploading…' : 'Choose PDFs or images'}
        </span>
        <span className="text-xs text-slate-400">You can select several at once · 50MB per file</span>
      </label>

      {error && <div className="text-xs text-rose-600 mt-3">{error}</div>}

      {isNew && pendingPromoFiles.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Ready to upload ({pendingPromoFiles.length})
          </div>
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {pendingPromoFiles.map((entry, i) => (
              <li key={`${entry.title}-${i}`} className="flex items-center gap-3 px-3 py-2.5">
                {/^application\/pdf$/i.test(entry.file.type)
                  ? <FileText size={15} className="text-rose-500 shrink-0" />
                  : <FileImage size={15} className="text-violet-500 shrink-0" />}
                <span className="text-sm text-slate-700 truncate flex-1">{entry.title}</span>
                <span className="text-xs text-slate-400 tabular-nums">{fmtSize(entry.file.size)}</span>
                <button
                  type="button"
                  onClick={() => setPendingPromoFiles((prev) => prev.filter((_, j) => j !== i))}
                  className="text-slate-400 hover:text-rose-600 transition"
                  title="Remove"
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 mt-2">
            These upload when you click <strong>Create course</strong>.
          </p>
        </div>
      )}

      {!isNew && (
        loading ? (
          <div className="text-sm text-slate-500 mt-4">Loading…</div>
        ) : materials.length === 0 ? (
          <div className="text-sm text-slate-500 mt-4">No promotional materials yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden mt-4">
            {materials.map((m) => (
              <li key={m.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50">
                {m.file_type === 'pdf'
                  ? <FileText size={15} className="text-rose-500 shrink-0" />
                  : <FileImage size={15} className="text-violet-500 shrink-0" />}

                {renamingId === m.id ? (
                  <input
                    autoFocus
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={() => saveRename(m)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); saveRename(m); }
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    className="input flex-1 py-1 text-sm"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => { setRenamingId(m.id); setRenameDraft(m.title); }}
                    className="text-sm text-slate-700 truncate flex-1 text-left hover:text-brand transition"
                    title="Click to rename"
                  >
                    {m.title}
                  </button>
                )}

                <span className="text-xs text-slate-400 tabular-nums hidden sm:inline">{fmtSize(m.file_size_bytes)}</span>
                <a
                  href={m.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-400 hover:text-brand transition"
                  title="Open in a new tab"
                >
                  <Download size={14} />
                </a>
                <button
                  type="button"
                  onClick={() => remove(m)}
                  className="text-slate-400 hover:text-rose-600 transition"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )
      )}
    </Card>
  );
}

function DripContentCard({ courseId, isNew, pendingDripFiles, setPendingDripFiles }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', content_type: 'pdf', delay_days: 7 });
  const [uploading, setUploading] = useState(null);
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const batchRef = useRef(null);

  const load = () => {
    if (isNew) return;
    api(`/api/admin/courses/${courseId}/drip-content`)
      .then(({ drip_items }) => setItems(drip_items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [courseId]);

  const addItem = async () => {
    if (!newItem.title.trim()) return;
    if (isNew) {
      setPendingDripFiles((prev) => [...prev, { ...newItem, key: `${Date.now()}-${Math.random()}` }]);
      setNewItem({ title: '', content_type: 'pdf', delay_days: 7 });
      setAdding(false);
      return;
    }
    const { drip_item } = await api(`/api/admin/courses/${courseId}/drip-content`, {
      method: 'POST',
      body: { ...newItem, display_order: items.length },
    });
    setItems((prev) => [...prev, drip_item]);
    setNewItem({ title: '', content_type: 'pdf', delay_days: 7 });
    setAdding(false);
  };

  const removeItem = async (itemId) => {
    await api(`/api/admin/drip-content/${itemId}`, { method: 'DELETE' });
    setItems((prev) => prev.filter((d) => d.id !== itemId));
  };

  const updateItem = async (itemId, updates) => {
    const { drip_item } = await api(`/api/admin/drip-content/${itemId}`, { method: 'PATCH', body: updates });
    setItems((prev) => prev.map((d) => (d.id === itemId ? drip_item : d)));
  };

  const uploadContent = async (itemId, contentType, file) => {
    setUploading(itemId);
    try {
      const fd = new FormData();
      const fieldName = contentType === 'scorm' ? 'scorm' : contentType;
      fd.append(fieldName, file);
      await apiUpload(`/api/admin/drip-content/${itemId}/${contentType}-upload`, fd);
      load();
    } finally {
      setUploading(null);
    }
  };

  const handleBatchFiles = (files) => {
    const entries = Array.from(files).map((file) => ({
      file,
      title: titleFromFilename(file.name),
      content_type: detectContentType(file),
      delay_days: 7,
      key: `${file.name}-${Date.now()}-${Math.random()}`,
    }));
    if (isNew) {
      setPendingDripFiles((prev) => [...prev, ...entries]);
    } else {
      setBatchFiles(entries);
    }
  };

  const updatePendingEntry = (key, updates) => {
    setPendingDripFiles((prev) => prev.map((e) => (e.key === key ? { ...e, ...updates } : e)));
  };

  const removePendingEntry = (key) => {
    setPendingDripFiles((prev) => prev.filter((e) => e.key !== key));
  };

  const updateBatchEntry = (key, updates) => {
    setBatchFiles((prev) => prev.map((e) => (e.key === key ? { ...e, ...updates } : e)));
  };

  const removeBatchEntry = (key) => {
    setBatchFiles((prev) => prev.filter((e) => e.key !== key));
  };

  const processBatch = async () => {
    if (!batchFiles.length) return;
    setBatchUploading(true);
    try {
      for (const entry of batchFiles) {
        const { drip_item } = await api(`/api/admin/courses/${courseId}/drip-content`, {
          method: 'POST',
          body: { title: entry.title, content_type: entry.content_type, delay_days: entry.delay_days, display_order: items.length },
        });
        const fd = new FormData();
        const fieldName = entry.content_type === 'scorm' ? 'scorm' : entry.content_type;
        fd.append(fieldName, entry.file);
        await apiUpload(`/api/admin/drip-content/${drip_item.id}/${entry.content_type}-upload`, fd);
      }
      setBatchFiles([]);
      load();
    } finally {
      setBatchUploading(false);
    }
  };

  const typeIcon = (t) => {
    if (t === 'scorm') return <BookOpen size={14} className="text-blue-500" />;
    if (t === 'video') return <Video size={14} className="text-purple-500" />;
    return <FileText size={14} className="text-rose-500" />;
  };

  const scheduleLabel = (days) => {
    if (days === 0) return 'Immediately after completion';
    if (days === 1) return '1 day after completion';
    return `${days} days after completion`;
  };

  const pendingList = isNew ? pendingDripFiles : [];

  return (
    <Card title="Drip content" icon={Sparkles}>
      <p className="text-sm text-slate-500 mb-4">
        Follow-up micro-learning materials delivered on a schedule after course completion.
      </p>

      {!isNew && loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (
        <>
          {/* Saved items (existing courses only) */}
          {items.length > 0 && (
            <div className="space-y-3 mb-4">
              {items.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <div className="mt-0.5">{typeIcon(item.content_type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-slate-900">{item.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                      <span>{item.content_type.toUpperCase()}</span>
                      <span>{scheduleLabel(item.delay_days)}</span>
                      {(item.content_url || item.scorm_folder_name) && (
                        <span className="text-emerald-600 flex items-center gap-0.5">
                          <CheckCircle size={10} /> Content uploaded
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <label className="text-xs text-slate-500">Delay:</label>
                      <select
                        value={item.delay_days}
                        onChange={(e) => updateItem(item.id, { delay_days: parseInt(e.target.value, 10) })}
                        className="text-xs border border-slate-200 rounded px-2 py-1"
                      >
                        <option value={0}>Immediately</option>
                        <option value={1}>1 day</option>
                        <option value={3}>3 days</option>
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                        <option value={21}>21 days</option>
                        <option value={30}>30 days</option>
                        <option value={60}>60 days</option>
                        <option value={90}>90 days</option>
                      </select>
                    </div>
                    {!item.content_url && !item.scorm_folder_name && (
                      <div className="mt-2">
                        <label className="relative overflow-hidden inline-flex items-center gap-1.5 text-xs font-medium text-brand hover:text-brand-dark cursor-pointer">
                          <Upload size={12} />
                          {uploading === item.id ? 'Uploading…' : `Upload ${item.content_type === 'scorm' ? '.zip' : item.content_type}`}
                          <input
                            type="file"
                            accept={item.content_type === 'scorm' ? '.zip' : item.content_type === 'pdf' ? '.pdf' : 'video/*'}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadContent(item.id, item.content_type, f);
                            }}
                            className="sr-only"
                            disabled={uploading === item.id}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors shrink-0"
                    title="Remove drip item"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Pending items (new courses — buffered until save) */}
          {pendingList.length > 0 && (
            <div className="space-y-2 mb-4">
              {pendingList.map((entry) => (
                <div key={entry.key} className="flex items-start gap-2 p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="mt-1.5">{typeIcon(entry.content_type)}</div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <input
                      type="text"
                      value={entry.title}
                      onChange={(e) => updatePendingEntry(entry.key, { title: e.target.value })}
                      className="w-full text-sm border border-slate-200 rounded px-2 py-1"
                      placeholder="Title"
                    />
                    <div className="flex flex-wrap gap-2 text-xs">
                      <select
                        value={entry.content_type}
                        onChange={(e) => updatePendingEntry(entry.key, { content_type: e.target.value })}
                        className="border border-slate-200 rounded px-2 py-1"
                      >
                        <option value="pdf">PDF</option>
                        <option value="video">Video</option>
                        <option value="scorm">SCORM</option>
                      </select>
                      <select
                        value={entry.delay_days}
                        onChange={(e) => updatePendingEntry(entry.key, { delay_days: parseInt(e.target.value, 10) })}
                        className="border border-slate-200 rounded px-2 py-1"
                      >
                        <option value={0}>Immediately</option>
                        <option value={1}>1 day</option>
                        <option value={3}>3 days</option>
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                        <option value={21}>21 days</option>
                        <option value={30}>30 days</option>
                        <option value={60}>60 days</option>
                        <option value={90}>90 days</option>
                      </select>
                      {entry.file && <span className="text-slate-400 truncate max-w-[200px]" title={entry.file.name}>{entry.file.name}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePendingEntry(entry.key)}
                    className="p-1 text-slate-400 hover:text-rose-500 shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <p className="text-xs text-slate-400 italic">These will be uploaded when you save the course.</p>
            </div>
          )}

          {/* Add / batch buttons */}
          {adding ? (
            <div className="p-4 rounded-lg border-2 border-dashed border-brand/30 bg-brand/5 space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Title</label>
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => setNewItem((n) => ({ ...n, title: e.target.value }))}
                    placeholder="e.g. Week 1 Refresher Quiz"
                    className="input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Type</label>
                  <select
                    value={newItem.content_type}
                    onChange={(e) => setNewItem((n) => ({ ...n, content_type: e.target.value }))}
                    className="input"
                  >
                    <option value="pdf">PDF</option>
                    <option value="video">Video</option>
                    <option value="scorm">SCORM</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Schedule</label>
                <select
                  value={newItem.delay_days}
                  onChange={(e) => setNewItem((n) => ({ ...n, delay_days: parseInt(e.target.value, 10) }))}
                  className="input sm:w-64"
                >
                  <option value={0}>Immediately after completion</option>
                  <option value={1}>1 day after completion</option>
                  <option value={3}>3 days after completion</option>
                  <option value={7}>7 days after completion</option>
                  <option value={14}>14 days after completion</option>
                  <option value={21}>21 days after completion</option>
                  <option value={30}>30 days after completion</option>
                  <option value={60}>60 days after completion</option>
                  <option value={90}>90 days after completion</option>
                </select>
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={addItem}
                  disabled={!newItem.title.trim()}
                  className="inline-flex items-center gap-1.5 bg-brand text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-brand-dark disabled:opacity-40"
                >
                  <Plus size={14} /> Add item
                </button>
                <button
                  type="button"
                  onClick={() => setAdding(false)}
                  className="text-sm text-slate-500 px-3 py-2 hover:text-slate-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-dark transition-colors"
              >
                <Plus size={14} /> Add single item
              </button>
              <label className="relative overflow-hidden inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-dark transition-colors cursor-pointer">
                <Upload size={14} /> Batch upload files
                <input
                  ref={batchRef}
                  type="file"
                  multiple
                  accept=".zip,.pdf,video/*"
                  onChange={(e) => { handleBatchFiles(e.target.files); e.target.value = ''; }}
                  className="sr-only"
                />
              </label>
            </div>
          )}

          {/* Batch review panel (existing courses — uploads immediately) */}
          {batchFiles.length > 0 && !isNew && (
            <div className="mt-4 p-4 rounded-lg border-2 border-dashed border-brand/30 bg-brand/5 space-y-3">
              <h4 className="text-sm font-semibold text-slate-700">Batch upload — {batchFiles.length} file{batchFiles.length !== 1 ? 's' : ''}</h4>
              <div className="space-y-2">
                {batchFiles.map((entry) => (
                  <div key={entry.key} className="flex items-start gap-2 p-2.5 rounded-lg bg-white border border-slate-200">
                    <div className="mt-1.5">{typeIcon(entry.content_type)}</div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <input
                        type="text"
                        value={entry.title}
                        onChange={(e) => updateBatchEntry(entry.key, { title: e.target.value })}
                        className="w-full text-sm border border-slate-200 rounded px-2 py-1"
                        placeholder="Title"
                      />
                      <div className="flex flex-wrap gap-2 text-xs">
                        <select
                          value={entry.content_type}
                          onChange={(e) => updateBatchEntry(entry.key, { content_type: e.target.value })}
                          className="border border-slate-200 rounded px-2 py-1"
                        >
                          <option value="pdf">PDF</option>
                          <option value="video">Video</option>
                          <option value="scorm">SCORM</option>
                        </select>
                        <select
                          value={entry.delay_days}
                          onChange={(e) => updateBatchEntry(entry.key, { delay_days: parseInt(e.target.value, 10) })}
                          className="border border-slate-200 rounded px-2 py-1"
                        >
                          <option value={0}>Immediately</option>
                          <option value={1}>1 day</option>
                          <option value={3}>3 days</option>
                          <option value={7}>7 days</option>
                          <option value={14}>14 days</option>
                          <option value={21}>21 days</option>
                          <option value={30}>30 days</option>
                          <option value={60}>60 days</option>
                          <option value={90}>90 days</option>
                        </select>
                        <span className="text-slate-400 truncate max-w-[200px]" title={entry.file.name}>{entry.file.name}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeBatchEntry(entry.key)}
                      className="p-1 text-slate-400 hover:text-rose-500 shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={processBatch}
                  disabled={batchUploading || batchFiles.some((e) => !e.title.trim())}
                  className="inline-flex items-center gap-1.5 bg-brand text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-brand-dark disabled:opacity-40"
                >
                  <Upload size={14} /> {batchUploading ? 'Uploading…' : `Upload all (${batchFiles.length})`}
                </button>
                <button
                  type="button"
                  onClick={() => setBatchFiles([])}
                  disabled={batchUploading}
                  className="text-sm text-slate-500 px-3 py-2 hover:text-slate-700 disabled:opacity-40"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function zoomSyncNotice(z) {
  if (z.error) {
    return {
      ok: false,
      text: `Course saved, but the Zoom meeting (${z.meeting_id}) couldn't be updated to match. Change its date, time and length in Zoom.`,
    };
  }
  const start = new Date(z.start_time);
  const end = new Date(start.getTime() + z.duration * 60000);
  const opts = { timeZone: 'America/New_York' };
  const day = start.toLocaleDateString('en-US', { ...opts, month: 'short', day: 'numeric' });
  const t = (d) => d.toLocaleTimeString('en-US', { ...opts, hour: 'numeric', minute: '2-digit' });
  return { ok: true, text: `Zoom meeting updated to match: ${day}, ${t(start)} – ${t(end)} ET.` };
}
